import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { RecordDialogProvider } from './contexts/RecordDialogContext';
import { PortalAuthProvider } from './portal/PortalAuthContext';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import RecordDialogHost from './components/RecordDialogHost';
import { lazyWithChunkReload } from './utils/lazyWithChunkReload';

// Shared cache for server data. Lookup lists (clients, products, ...) are
// cached briefly so opening a dialog twice doesn't refetch everything.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Lazy load other pages. Entity detail components (ClientDetail, AssetDetail, etc.)
// are no longer registered as full-page routes; they are loaded on demand by
// RecordDialogHost when openRecord is called.
const Clients = lazyWithChunkReload(() => import('./pages/Clients'));
const Contacts = lazyWithChunkReload(() => import('./pages/Contacts'));
const Tickets = lazyWithChunkReload(() => import('./pages/Tickets'));
const Projects = lazyWithChunkReload(() => import('./pages/Projects'));
const Sites = lazyWithChunkReload(() => import('./pages/Sites'));
const Assets = lazyWithChunkReload(() => import('./pages/Assets'));
const Quotes = lazyWithChunkReload(() => import('./pages/Quotes'));
const Invoices = lazyWithChunkReload(() => import('./pages/Invoices'));
const Products = lazyWithChunkReload(() => import('./pages/Products'));
const Vendors = lazyWithChunkReload(() => import('./pages/Vendors'));
const Services = lazyWithChunkReload(() => import('./pages/Services'));
const ServerFunctions = lazyWithChunkReload(() => import('./pages/ServerFunctions'));
const Renewals = lazyWithChunkReload(() => import('./pages/Renewals'));
const Signatures = lazyWithChunkReload(() => import('./pages/Signatures'));
const Integrations = lazyWithChunkReload(() => import('./pages/Integrations'));
const M365Tenants = lazyWithChunkReload(() => import('./pages/M365Tenants'));
const Payments = lazyWithChunkReload(() => import('./pages/Payments'));
const ClientCredits = lazyWithChunkReload(() => import('./pages/ClientCredits'));
const Credentials = lazyWithChunkReload(() => import('./pages/Credentials'));
const Users = lazyWithChunkReload(() => import('./pages/Users'));
const Settings = lazyWithChunkReload(() => import('./pages/Settings'));
const EmailSettings = lazyWithChunkReload(() => import('./pages/EmailSettings'));
const BrandingSettings = lazyWithChunkReload(() => import('./pages/BrandingSettings'));
const PasswordManagerSettings = lazyWithChunkReload(() => import('./pages/PasswordManagerSettings'));
const Profile = lazyWithChunkReload(() => import('./pages/Profile'));
const Backups = lazyWithChunkReload(() => import('./pages/Backups'));
const ManagedBackups = lazyWithChunkReload(() => import('./pages/ManagedBackups'));
const Monitoring = lazyWithChunkReload(() => import('./pages/Monitoring'));
const Rmm = lazyWithChunkReload(() => import('./pages/Rmm'));
const Handover = lazyWithChunkReload(() => import('./pages/Handover'));
const CyberEssentials = lazyWithChunkReload(() => import('./pages/CyberEssentials'));
const KnowledgeBase = lazyWithChunkReload(() => import('./pages/KnowledgeBase'));
const Support = lazyWithChunkReload(() => import('./pages/Support'));
const PublicForm = lazyWithChunkReload(() => import('./pages/PublicForm'));
const CeQuestionnaire = lazyWithChunkReload(() => import('./pages/CeQuestionnaire'));
const RemoteSupportSettings = lazyWithChunkReload(() => import('./pages/RemoteSupportSettings'));
const AISettings = lazyWithChunkReload(() => import('./pages/AISettings'));
const PaymentSettings = lazyWithChunkReload(() => import('./pages/PaymentSettings'));
const PrepaidSettings = lazyWithChunkReload(() => import('./pages/PrepaidSettings'));

const PortalLogin = lazyWithChunkReload(() => import('./portal/PortalLogin'));
const PortalVerify = lazyWithChunkReload(() => import('./portal/PortalVerify'));
const PortalLayout = lazyWithChunkReload(() => import('./portal/PortalLayout'));
const PortalDashboard = lazyWithChunkReload(() => import('./portal/PortalDashboard'));
const PortalUptime = lazyWithChunkReload(() => import('./portal/PortalUptime'));
const PortalCyberEssentialsReadiness = lazyWithChunkReload(() => import('./portal/PortalCyberEssentialsReadiness'));
const PortalTickets = lazyWithChunkReload(() => import('./portal/PortalTickets'));
const PortalTicketDetail = lazyWithChunkReload(() => import('./portal/PortalTicketDetail'));
const PortalInvoices = lazyWithChunkReload(() => import('./portal/PortalInvoices'));
const PortalInvoiceDetail = lazyWithChunkReload(() => import('./portal/PortalInvoiceDetail'));
const PortalQuotes = lazyWithChunkReload(() => import('./portal/PortalQuotes'));
const PortalQuoteDetail = lazyWithChunkReload(() => import('./portal/PortalQuoteDetail'));
const PortalBuyTime = lazyWithChunkReload(() => import('./portal/PortalBuyTime'));

const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '50vh',
    color: 'var(--text-muted, #737373)'
  }}>
    Loading...
  </div>
);

// The original palette, applied before anything renders. An instance that
// configures no colours therefore looks exactly as it always has.
const BRAND_FALLBACK = {
  // The literals belong here and ONLY here - this is what defines the variables
  // the rest of the app references, so it cannot itself be a reference.
  primaryColor: '#ba232e',
  primaryColorDark: '#9c1d27',
  primaryColorLight: '#d42a37'
};

function applyBrandColours(theme) {
  const root = document.documentElement;
  root.style.setProperty('--brand-primary',
    theme?.primaryColor || BRAND_FALLBACK.primaryColor);
  root.style.setProperty('--brand-primary-dark',
    theme?.primaryColorDark || BRAND_FALLBACK.primaryColorDark);
  root.style.setProperty('--brand-primary-light',
    theme?.primaryColorLight || BRAND_FALLBACK.primaryColorLight);
}

// NOTHING is applied here any more. Setting the default inline at module load
// is what made a CF-branded instance paint red and then turn blue: an inline
// style beats the stylesheet, so JS was actively overriding the correct colour
// with the fallback. The variables now come from /api/public/branding.css,
// which the browser blocks on.

function App() {
  React.useEffect(() => {
    const updateFavicon = async () => {
      try {
        const response = await fetch('/api/public/branding', { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        // The container bakes the title into index.html at startup, so this is
        // only a fallback for a deployment that does not (a dev server, say).
        const product = data?.productName || data?.brandName;
        if (product && product !== 'Zone') {
          document.title = data?.companyName && data.companyName !== product
            ? `${product} - ${data.companyName}`
            : product;
        }

        // Belt and braces only: if the stylesheet failed to load, this
        // still gets the colour right, just a beat later.
        if (!getComputedStyle(document.documentElement)
              .getPropertyValue('--brand-primary').trim()) {
          applyBrandColours(data);
        }
        if (!data?.favicon?.configured || !data?.favicon?.url) return;

        const href = `${data.favicon.url}?v=${Date.now()}`;
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'icon');
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      } catch {
        // Ignore favicon fetch failures.
      }
    };

    updateFavicon();
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <RecordDialogProvider>
              <CssBaseline />
              <RecordDialogHost />
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/form/:token"
                element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <PublicForm />
                  </React.Suspense>
                }
              />
              {/* The Cyber Essentials questionnaire a client answers from a
                  link — no Zone login, and they need not be a Zone client. */}
              <Route
                path="/ce/:token"
                element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CeQuestionnaire />
                  </React.Suspense>
                }
              />
              <Route
                path="/support"
                element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Support />
                  </React.Suspense>
                }
              />
              <Route
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/clients" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Clients />
                  </React.Suspense>
                } />
                <Route path="/contacts" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Contacts />
                  </React.Suspense>
                } />
                <Route path="/sites" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Sites />
                  </React.Suspense>
                } />
                <Route path="/tickets" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Tickets />
                  </React.Suspense>
                } />
                <Route path="/projects" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Projects />
                  </React.Suspense>
                } />
                <Route path="/assets" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Assets />
                  </React.Suspense>
                } />
                <Route path="/quotes" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Quotes />
                  </React.Suspense>
                } />
                <Route path="/invoices" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Invoices />
                  </React.Suspense>
                } />
                <Route path="/products" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Products />
                  </React.Suspense>
                } />
                <Route path="/vendors" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Vendors />
                  </React.Suspense>
                } />
                <Route path="/services" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Services />
                  </React.Suspense>
                } />
                <Route path="/server-functions" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <ServerFunctions />
                  </React.Suspense>
                } />
                <Route path="/renewals" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Renewals />
                  </React.Suspense>
                } />
                <Route path="/signatures" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Signatures />
                  </React.Suspense>
                } />
                <Route path="/settings/integrations" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Integrations />
                  </React.Suspense>
                } />
                <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
                <Route path="/payments" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Payments />
                  </React.Suspense>
                } />
                <Route path="/clients/:clientId/credits" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <ClientCredits />
                  </React.Suspense>
                } />
                <Route path="/credentials" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Credentials />
                  </React.Suspense>
                } />
                <Route path="/users" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Users />
                  </React.Suspense>
                } />
                <Route path="/settings" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Settings />
                  </React.Suspense>
                } />
                <Route path="/settings/email" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EmailSettings />
                  </React.Suspense>
                } />
                <Route path="/settings/branding" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <BrandingSettings />
                  </React.Suspense>
                } />
                <Route path="/settings/password-manager" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <PasswordManagerSettings />
                  </React.Suspense>
                } />
                <Route path="/settings/remote-support" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <RemoteSupportSettings />
                  </React.Suspense>
                } />
                <Route path="/settings/ai" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <AISettings />
                  </React.Suspense>
                } />
                <Route path="/settings/payments" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <PaymentSettings />
                  </React.Suspense>
                } />
                <Route path="/settings/prepaid" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <PrepaidSettings />
                  </React.Suspense>
                } />
                <Route path="/profile" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Profile />
                  </React.Suspense>
                } />
                <Route path="/backups" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Backups />
                  </React.Suspense>
                } />
                <Route path="/monitoring" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Monitoring />
                  </React.Suspense>
                } />
                <Route path="/rmm" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Rmm />
                  </React.Suspense>
                } />
                <Route path="/managed-backups" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <ManagedBackups />
                  </React.Suspense>
                } />
                <Route path="/settings/microsoft-365" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <M365Tenants />
                  </React.Suspense>
                } />
                <Route path="/m365" element={<Navigate to="/settings/microsoft-365" replace />} />
                <Route path="/handover" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Handover />
                  </React.Suspense>
                } />
                <Route path="/cyber-essentials" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CyberEssentials />
                  </React.Suspense>
                } />
                <Route path="/knowledge-base" element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <KnowledgeBase />
                  </React.Suspense>
                } />
              </Route>
              <Route
                path="/portal/*"
                element={
                  <PortalAuthProvider>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route index element={<PortalLogin />} />
                        <Route path="auth/verify" element={<PortalVerify />} />
                        <Route element={<PortalLayout />}>
                          <Route path="dashboard" element={<PortalDashboard />} />
                          <Route path="uptime" element={<PortalUptime />} />
                          <Route path="cyber-essentials" element={<PortalCyberEssentialsReadiness />} />
                          <Route path="tickets" element={<PortalTickets />} />
                          <Route path="tickets/:id" element={<PortalTicketDetail />} />
                          <Route path="invoices" element={<PortalInvoices />} />
                          <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
                          <Route path="quotes" element={<PortalQuotes />} />
                          <Route path="quotes/:id" element={<PortalQuoteDetail />} />
                          <Route path="buy-time" element={<PortalBuyTime />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/portal" replace />} />
                      </Routes>
                    </React.Suspense>
                  </PortalAuthProvider>
                }
              />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </RecordDialogProvider>
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
